import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import InvoiceItemsEditor from "@/components/InvoiceItemsEditor";
import {
  CheckCircle,
  AlertTriangle,
  Save,
  ArrowLeft,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { deriveGstBreakdown } from "@/utils/gst";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const hasInitializedRef = useRef(false);
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isOrganizationClient = storedUser.account_type === "client";
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifyScanning, setVerifyScanning] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [companyType, setCompanyType] = useState("");
  const [companyGstNo, setCompanyGstNo] = useState("");
  const [masterItems, setMasterItems] = useState([]);
  const [companyProfile, setCompanyProfile] = useState({
    company_name: "",
    company_gst_no: "",
    address: "",
    contact_person: "",
    contact_number: "",
  });
  const [formData, setFormData] = useState({
    invoice_no: "",
    invoice_date: "",
    supplier_name: "",
    supplier_address: "",
    supplier_gst_no: "",
    supplier_contact_person: "",
    supplier_contact_number: "",
    buyer_name: "",
    buyer_address: "",
    buyer_gst_no: "",
    buyer_contact_person: "",
    buyer_contact_number: "",
    address: "",
    gst_no: "",
    contact_person: "",
    contact_number: "",
    basic_amount: 0,
    discount_amount: 0,
    net_payable: 0,
    gross_amount: 0,
    gst: 0,
    gst_manual_override: false,
    total_amount: 0,
    gst_rate: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    items: [],
  });

  const applyOwnCompanyDetails = useCallback(
    (invoiceType, currentData, options = {}) => {
      const { force = false } = options;
      if (!invoiceType) {
        return currentData;
      }

      const nextData = { ...currentData };
      const resolveValue = (currentValue, companyValue) =>
        force ? companyValue || currentValue || "" : currentValue || companyValue || "";

      if (invoiceType === "purchase") {
        nextData.buyer_name = resolveValue(
          nextData.buyer_name,
          companyProfile.company_name,
        );
        nextData.buyer_address = resolveValue(
          nextData.buyer_address,
          companyProfile.address,
        );
        nextData.buyer_gst_no = resolveValue(
          nextData.buyer_gst_no,
          companyProfile.company_gst_no,
        );
        nextData.buyer_contact_person = resolveValue(
          nextData.buyer_contact_person,
          companyProfile.contact_person,
        );
        nextData.buyer_contact_number = resolveValue(
          nextData.buyer_contact_number,
          companyProfile.contact_number,
        );
      } else {
        nextData.supplier_name = resolveValue(
          nextData.supplier_name,
          companyProfile.company_name,
        );
        nextData.supplier_address = resolveValue(
          nextData.supplier_address,
          companyProfile.address,
        );
        nextData.supplier_gst_no = resolveValue(
          nextData.supplier_gst_no,
          companyProfile.company_gst_no,
        );
        nextData.supplier_contact_person = resolveValue(
          nextData.supplier_contact_person,
          companyProfile.contact_person,
        );
        nextData.supplier_contact_number = resolveValue(
          nextData.supplier_contact_number,
          companyProfile.contact_number,
        );
        nextData.address = nextData.supplier_address;
        nextData.gst_no = nextData.supplier_gst_no;
        nextData.contact_person = nextData.supplier_contact_person;
        nextData.contact_number = nextData.supplier_contact_number;
      }

      return nextData;
    },
    [companyProfile],
  );

  const hydrateInvoiceState = useCallback((invoiceData) => {
    setInvoice(invoiceData);
    const hydratedData = {
      invoice_no: invoiceData.extracted_data?.invoice_no || "",
      invoice_date: invoiceData.extracted_data?.invoice_date || "",
      supplier_name: invoiceData.extracted_data?.supplier_name || "",
      supplier_address: invoiceData.extracted_data?.supplier_address || "",
      supplier_gst_no: invoiceData.extracted_data?.supplier_gst_no || "",
      supplier_contact_person:
        invoiceData.extracted_data?.supplier_contact_person || "",
      supplier_contact_number:
        invoiceData.extracted_data?.supplier_contact_number || "",
      buyer_name: invoiceData.extracted_data?.buyer_name || "",
      buyer_address: invoiceData.extracted_data?.buyer_address || "",
      buyer_gst_no: invoiceData.extracted_data?.buyer_gst_no || "",
      buyer_contact_person:
        invoiceData.extracted_data?.buyer_contact_person || "",
      buyer_contact_number:
        invoiceData.extracted_data?.buyer_contact_number || "",
      address: invoiceData.extracted_data?.address || "",
      gst_no: invoiceData.extracted_data?.gst_no || "",
      contact_person: invoiceData.extracted_data?.contact_person || "",
      contact_number: invoiceData.extracted_data?.contact_number || "",
      basic_amount: invoiceData.extracted_data?.basic_amount || 0,
      discount_amount: invoiceData.extracted_data?.discount_amount || 0,
      net_payable: invoiceData.extracted_data?.net_payable || 0,
      gross_amount:
        invoiceData.extracted_data?.gross_amount ||
        invoiceData.extracted_data?.total_amount ||
        0,
      gst: invoiceData.extracted_data?.gst || 0,
      gst_manual_override: false,
      total_amount: invoiceData.extracted_data?.total_amount || 0,
      gst_rate: invoiceData.extracted_data?.gst_rate || 0,
      cgst: invoiceData.extracted_data?.cgst || 0,
      sgst: invoiceData.extracted_data?.sgst || 0,
      igst: invoiceData.extracted_data?.igst || 0,
      items: invoiceData.extracted_data?.items || [],
    };
    setFormData(
      applyOwnCompanyDetails(invoiceData.invoice_type, hydratedData, {
        force: true,
      }),
    );
  }, [applyOwnCompanyDetails]);

  const loadCompanySettings = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const [settingsResponse, itemMasterResponse] = await Promise.all([
        axios.get(`${API}/settings/company`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/inventory/items`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setCompanyType(settingsResponse.data?.company_type || "");
      setCompanyGstNo(settingsResponse.data?.company_gst_no || "");
      setCompanyProfile({
        company_name: settingsResponse.data?.company_name || "",
        company_gst_no: settingsResponse.data?.company_gst_no || "",
        address: settingsResponse.data?.address || "",
        contact_person: settingsResponse.data?.contact_person || "",
        contact_number: settingsResponse.data?.contact_number || "",
      });
      setMasterItems(itemMasterResponse.data || []);
    } catch (error) {
      setCompanyType("");
      setCompanyGstNo("");
      setMasterItems([]);
      setCompanyProfile({
        company_name: "",
        company_gst_no: "",
        address: "",
        contact_person: "",
        contact_number: "",
      });
    }
  }, []);

  const loadInvoice = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      hydrateInvoiceState(response.data);
      setLoading(false);
      return response.data;
    } catch (error) {
      toast.error("Failed to load invoice");
      navigate("/invoices");
      return null;
    }
  }, [hydrateInvoiceState, id, navigate]);

  const runVerifyScan = useCallback(async (forceRescan = false) => {
    try {
      setVerifyScanning(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/invoices/${id}/verify-scan`,
        { force: forceRescan },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data?.invoice) {
        hydrateInvoiceState(response.data.invoice);
      }

      if (forceRescan && !response.data?.skipped) {
        toast.success("Invoice re-verified with full AI scan");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to run full AI verify scan",
      );
    } finally {
      setVerifyScanning(false);
    }
  }, [hydrateInvoiceState, id]);

  // This page should initialize once per invoice id; the ref prevents later
  // callback identity changes from reloading and overwriting active edits.
  useEffect(() => {
    if (!invoice?.invoice_type || !companyProfile.company_name) {
      return;
    }

    setFormData((prev) => {
      const next = applyOwnCompanyDetails(invoice.invoice_type, prev);
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [applyOwnCompanyDetails, companyProfile.company_name, invoice?.invoice_type]);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;

    const initializePage = async () => {
      await loadCompanySettings();
      const invoiceData = await loadInvoice();
      if (
        invoiceData &&
        !invoiceData.is_manual_entry &&
        invoiceData.ai_extraction_stage !== "full"
      ) {
        await runVerifyScan();
      }
    };

    initializePage();
  }, [id, loadCompanySettings, loadInvoice, runVerifyScan]);

  const handleSave = async (markAsVerified = false) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const preparedFormData = applyOwnCompanyDetails(
        invoice.invoice_type,
        formData,
      );
      const derivedFormData = {
        ...preparedFormData,
        ...deriveGstBreakdown({
          invoiceType: invoice.invoice_type,
          companyGstNo,
          extractedData: preparedFormData,
        }),
      };
      await axios.put(
        `${API}/invoices/${id}`,
        {
          extracted_data: derivedFormData,
          status: markAsVerified ? "verified" : invoice.status,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        markAsVerified ? "Invoice verified successfully!" : "Changes saved",
      );
      if (markAsVerified) {
        navigate("/invoices");
      } else {
        loadInvoice();
      }
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const getConfidenceColor = (fieldName) => {
    if (!invoice) return "";
    const confidence = invoice.confidence_scores?.[fieldName] || 0;
    if (confidence >= 0.9) return "border-[#10B981] bg-[#10B981]/5";
    if (confidence >= 0.7) return "border-[#F59E0B] bg-[#F59E0B]/5";
    return "border-[#EF4444] bg-[#EF4444]/5";
  };

  const getConfidenceBadge = (fieldName) => {
    if (!invoice) return null;
    const confidence = invoice.confidence_scores?.[fieldName] || 0;
    const percentage = Math.round(confidence * 100);

    if (confidence >= 0.9) {
      return (
        <Badge className="bg-[#10B981] text-white ml-2">{percentage}%</Badge>
      );
    } else if (confidence >= 0.7) {
      return (
        <Badge className="bg-[#F59E0B] text-white ml-2">{percentage}%</Badge>
      );
    } else {
      return (
        <Badge className="bg-[#EF4444] text-white ml-2">{percentage}%</Badge>
      );
    }
  };

  const companyGstFieldError =
    invoice?.validation_flags?.gst_mismatch &&
    invoice?.validation_flags?.gst_validation_remark
      ? invoice.validation_flags.gst_validation_remark
      : "";

  const customerMasterGstFieldError =
    invoice?.validation_flags?.customer_master_gst_mismatch &&
    invoice?.validation_flags?.customer_master_remark
      ? invoice.validation_flags.customer_master_remark
      : "";

  const companyGstFieldClass = companyGstFieldError
    ? "border-[#EF4444] bg-[#EF4444]/5 text-[#B91C1C] focus-visible:ring-[#EF4444]"
    : "";

  const customerMasterGstFieldClass = customerMasterGstFieldError
    ? "border-[#EF4444] bg-[#EF4444]/5 text-[#B91C1C] focus-visible:ring-[#EF4444]"
    : "";

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12" data-testid="loading-message">
          Loading invoice...
        </div>
      </Layout>
    );
  }

  const avgConfidence =
    Object.values(invoice.confidence_scores || {}).reduce((a, b) => a + b, 0) /
    8;
  const derivedBreakdown = deriveGstBreakdown({
    invoiceType: invoice.invoice_type,
    companyGstNo,
    extractedData: applyOwnCompanyDetails(invoice.invoice_type, formData),
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="verify-invoice-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button
              onClick={() => navigate("/invoices")}
              variant="ghost"
              data-testid="back-btn"
              className="mb-2 -ml-2 text-[#0B2B5C] hover:text-[#0B2B5C] hover:bg-[#0B2B5C]/5"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Invoices
            </Button>
            <h1
              className="text-lg font-manrope font-bold text-[#0B2B5C]"
              data-testid="verify-invoice-title"
            >
              Cross-Verify Invoice
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Review and edit extracted data. Fields highlighted in color
              indicate confidence levels.
            </p>
          </div>
          {!isOrganizationClient && (
            <div className="flex gap-2">
              {!invoice.is_manual_entry &&
                invoice.ai_extraction_stage === "full" && (
                  <Button
                    onClick={() => runVerifyScan(true)}
                    variant="outline"
                    disabled={verifyScanning || saving}
                    data-testid="reverify-btn"
                    className="border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-white"
                  >
                    Re-verify
                  </Button>
                )}
              <Button
                onClick={() => handleSave(false)}
                variant="outline"
                data-testid="save-changes-btn"
                disabled={saving || verifyScanning}
                className="border-[#0B2B5C] text-[#0B2B5C] hover:bg-[#0B2B5C] hover:text-white"
              >
                <Save size={16} className="mr-2" />
                Save Changes
              </Button>
              <Button
                onClick={() => handleSave(true)}
                data-testid="verify-submit-btn"
                disabled={saving || verifyScanning}
                className="bg-[#10B981] hover:bg-[#10B981]/90 text-white"
              >
                <CheckCircle size={16} className="mr-2" />
                Verify & Approve
              </Button>
            </div>
          )}
        </div>

        {/* Confidence Alert */}
        {avgConfidence < 0.8 && (
          <Alert
            className="border-[#F59E0B] bg-[#F59E0B]/5"
            data-testid="low-confidence-alert"
          >
            <AlertTriangle className="h-4 w-4 text-[#F59E0B]" />
            <AlertDescription className="text-[#F59E0B]">
              Average confidence: {Math.round(avgConfidence * 100)}%. Please
              carefully review all fields before approving.
            </AlertDescription>
          </Alert>
        )}

        {verifyScanning && (
          <Alert className="border-[#0B2B5C] bg-[#0B2B5C]/5">
            <AlertTriangle className="h-4 w-4 text-[#0B2B5C]" />
            <AlertDescription className="text-[#0B2B5C]">
              Full AI verify scan is running now. Upload saved the basic invoice
              data quickly, and this screen is now loading the complete
              extraction for review.
            </AlertDescription>
          </Alert>
        )}

        {!invoice.is_manual_entry &&
          invoice.ai_extraction_stage === "full" &&
          !verifyScanning && (
            <Alert className="border-[#10B981] bg-[#10B981]/5">
              <CheckCircle className="h-4 w-4 text-[#10B981]" />
              <AlertDescription className="text-[#10B981]">
                Full AI verify scan is already available for this invoice.
                It will not scan again automatically. Use Re-verify only when
                you want to run the AI again.
              </AlertDescription>
            </Alert>
          )}

        {invoice?.inventory_sync?.skipped_reason && (
          <Alert className="border-[#F59E0B] bg-[#F59E0B]/5">
            <AlertTriangle className="h-4 w-4 text-[#F59E0B]" />
            <AlertDescription className="text-[#92400E]">
              Inventory status: {invoice.inventory_sync.skipped_reason}
            </AlertDescription>
          </Alert>
        )}

        {invoice?.inventory_sync?.synced && (
          <Alert className="border-[#10B981] bg-[#10B981]/5">
            <CheckCircle className="h-4 w-4 text-[#10B981]" />
            <AlertDescription className="text-[#065F46]">
              Inventory synced for this verified invoice.
            </AlertDescription>
          </Alert>
        )}

        {/* Split View */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Invoice Preview */}
          <Card className="border-[#0B2B5C]/10 shadow-lg lg:sticky lg:top-6 lg:h-fit">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-manrope text-[#0B2B5C]">
                  Invoice Preview
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
                    data-testid="zoom-out-btn"
                  >
                    <ZoomOut size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setZoom(Math.min(2, zoom + 0.1))}
                    data-testid="zoom-in-btn"
                  >
                    <ZoomIn size={16} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto max-h-[800px] bg-muted/30 rounded-sm p-4">
                {invoice.file_type === "application/pdf" ? (
                  <embed
                    src={invoice.file_url || `data:application/pdf;base64,${invoice.file_data}`}
                    type="application/pdf"
                    data-testid="pdf-preview"
                    width="100%"
                    height="800px"
                    style={{
                      transform: `scale(${zoom})`,
                      transformOrigin: "top left",
                    }}
                  />
                ) : (
                  <img
                    src={invoice.file_url || `data:${invoice.file_type};base64,${invoice.file_data}`}
                    alt="Invoice"
                    data-testid="image-preview"
                    className="max-w-full h-auto rounded-sm"
                    style={{
                      transform: `scale(${zoom})`,
                      transformOrigin: "top left",
                    }}
                  />
                )}
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                <p data-testid="invoice-filename">File: {invoice.filename}</p>
                <p data-testid="invoice-uploaded-date">
                  Uploaded: {new Date(invoice.created_at).toLocaleString()}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Right Panel - Edit Form */}
          <Card className="border-[#0B2B5C]/10 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg font-manrope text-[#0B2B5C]">
                Extracted Data
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Edit any field that needs correction
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Invoice Details */}
              <div className="space-y-4">
                <h3 className="font-manrope font-semibold text-[#0B2B5C]">
                  Invoice Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="invoice_no" className="flex items-center">
                      Invoice Number
                      {getConfidenceBadge("invoice_no")}
                    </Label>
                    <Input
                      id="invoice_no"
                      data-testid="invoice-no-input"
                      value={formData.invoice_no || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, invoice_no: e.target.value })
                      }
                      className={`font-mono mt-1 ${getConfidenceColor("invoice_no")}`}
                    />
                  </div>
                  <div>
                    <Label htmlFor="invoice_date" className="flex items-center">
                      Invoice Date
                      {getConfidenceBadge("invoice_date")}
                    </Label>
                    <Input
                      id="invoice_date"
                      data-testid="invoice-date-input"
                      value={formData.invoice_date || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          invoice_date: e.target.value,
                        })
                      }
                      className={`mt-1 ${getConfidenceColor("invoice_date")}`}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Bill From / Supplier Information */}
              <div className="space-y-4">
                <h3 className="font-manrope font-semibold text-[#0B2B5C]">
                  {invoice.invoice_type === "sales"
                    ? "Bill From (Your Company)"
                    : "Bill From (Supplier/Vendor)"}
                </h3>
                <div>
                  <Label htmlFor="supplier_name" className="flex items-center">
                    Supplier Name
                    {getConfidenceBadge("supplier_name")}
                  </Label>
                  <Input
                    id="supplier_name"
                    data-testid="supplier-name-input"
                    value={formData.supplier_name || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        supplier_name: e.target.value,
                      })
                    }
                    className={`mt-1 ${getConfidenceColor("supplier_name")}`}
                  />
                </div>
                <div>
                  <Label
                    htmlFor="supplier_address"
                    className="flex items-center"
                  >
                    Supplier Address
                  </Label>
                  <Input
                    id="supplier_address"
                    data-testid="supplier-address-input"
                    value={formData.supplier_address || formData.address || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        supplier_address: e.target.value,
                        address: e.target.value,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="supplier_gst_no"
                    className="flex items-center"
                  >
                    Supplier GST Number
                    {getConfidenceBadge("gst_no")}
                  </Label>
                  <Input
                    id="supplier_gst_no"
                    data-testid="supplier-gst-no-input"
                    value={formData.supplier_gst_no || formData.gst_no || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        supplier_gst_no: e.target.value,
                        gst_no: e.target.value,
                      })
                    }
                    className={`font-mono mt-1 ${
                      invoice.invoice_type === "sales"
                        ? companyGstFieldClass || getConfidenceColor("gst_no")
                        : customerMasterGstFieldClass || getConfidenceColor("gst_no")
                    }`}
                  />
                  {invoice.invoice_type === "sales" && companyGstFieldError && (
                    <p className="mt-1 text-xs font-medium text-[#EF4444]">
                      {companyGstFieldError}
                    </p>
                  )}
                  {invoice.invoice_type === "purchase" &&
                    customerMasterGstFieldError && (
                    <p className="mt-1 text-xs font-medium text-[#EF4444]">
                      {customerMasterGstFieldError}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="supplier_contact_person">
                      Contact Person
                    </Label>
                    <Input
                      id="supplier_contact_person"
                      data-testid="supplier-contact-person-input"
                      value={
                        formData.supplier_contact_person ||
                        formData.contact_person ||
                        ""
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          supplier_contact_person: e.target.value,
                          contact_person: e.target.value,
                        })
                      }
                      className="mt-1"
                      placeholder="Name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="supplier_contact_number">
                      Contact Number
                    </Label>
                    <Input
                      id="supplier_contact_number"
                      data-testid="supplier-contact-number-input"
                      value={
                        formData.supplier_contact_number ||
                        formData.contact_number ||
                        ""
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          supplier_contact_number: e.target.value,
                          contact_number: e.target.value,
                        })
                      }
                      className="mt-1"
                      placeholder="+91 9876543210"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Bill To / Buyer Information */}
              <div className="space-y-4">
                <h3 className="font-manrope font-semibold text-[#0B2B5C]">
                  {invoice.invoice_type === "sales"
                    ? "Bill To (Customer/Buyer)"
                    : "Bill To (Your Company)"}
                </h3>
                <div>
                  <Label htmlFor="buyer_name" className="flex items-center">
                    Buyer Name
                  </Label>
                  <Input
                    id="buyer_name"
                    data-testid="buyer-name-input"
                    value={formData.buyer_name || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, buyer_name: e.target.value })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="buyer_address" className="flex items-center">
                    Buyer Address
                  </Label>
                  <Input
                    id="buyer_address"
                    data-testid="buyer-address-input"
                    value={formData.buyer_address || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        buyer_address: e.target.value,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="buyer_gst_no" className="flex items-center">
                    Buyer GST Number
                  </Label>
                  <Input
                    id="buyer_gst_no"
                    data-testid="buyer-gst-no-input"
                    value={formData.buyer_gst_no || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, buyer_gst_no: e.target.value })
                    }
                    className={`font-mono mt-1 ${
                      invoice.invoice_type === "purchase"
                        ? companyGstFieldClass
                        : customerMasterGstFieldClass
                    }`}
                  />
                  {invoice.invoice_type === "purchase" && companyGstFieldError && (
                    <p className="mt-1 text-xs font-medium text-[#EF4444]">
                      {companyGstFieldError}
                    </p>
                  )}
                  {invoice.invoice_type === "sales" &&
                    customerMasterGstFieldError && (
                    <p className="mt-1 text-xs font-medium text-[#EF4444]">
                      {customerMasterGstFieldError}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="buyer_contact_person">Contact Person</Label>
                    <Input
                      id="buyer_contact_person"
                      data-testid="buyer-contact-person-input"
                      value={formData.buyer_contact_person || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          buyer_contact_person: e.target.value,
                        })
                      }
                      className="mt-1"
                      placeholder="Name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="buyer_contact_number">Contact Number</Label>
                    <Input
                      id="buyer_contact_number"
                      data-testid="buyer-contact-number-input"
                      value={formData.buyer_contact_number || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          buyer_contact_number: e.target.value,
                        })
                      }
                      className="mt-1"
                      placeholder="+91 9876543210"
                    />
                  </div>
                </div>
              </div>

              <InvoiceItemsEditor
                items={formData.items || []}
                onChange={(items) =>
                  setFormData({
                    ...formData,
                    items,
                    gst_manual_override: false,
                  })
                }
                companyType={companyType}
                masterItems={masterItems}
              />

              <Separator />

              {/* Amount Details */}
              <div className="space-y-4">
                <h3 className="font-manrope font-semibold text-[#0B2B5C]">
                  Amount Details
                </h3>
                <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 items-end">
                  <div className="min-w-0">
                    <Label
                      htmlFor="basic_amount"
                      className="flex items-center min-h-10"
                    >
                      Basic Amount
                      {getConfidenceBadge("basic_amount")}
                    </Label>
                    <Input
                      id="basic_amount"
                      type="number"
                      data-testid="basic-amount-input"
                      value={derivedBreakdown.basic_amount || 0}
                      readOnly
                      className={`font-mono mt-1 bg-muted/40 ${getConfidenceColor("basic_amount")}`}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label
                      htmlFor="discount_amount"
                      className="flex items-center min-h-10 whitespace-nowrap"
                    >
                      Discount Amount
                    </Label>
                    <Input
                      id="discount_amount"
                      type="number"
                      value={derivedBreakdown.discount_amount || 0}
                      readOnly
                      className="font-mono mt-1 bg-muted/40"
                      step="0.01"
                    />
                  </div>
                  <div className="min-w-0">
                    <Label
                      htmlFor="net_payable"
                      className="flex items-center min-h-10 whitespace-nowrap"
                    >
                      Net Payable
                    </Label>
                    <Input
                      id="net_payable"
                      type="number"
                      value={derivedBreakdown.net_payable || 0}
                      readOnly
                      className="font-mono mt-1 bg-muted/40"
                    />
                  </div>
                  <div className="min-w-0">
                    <Label htmlFor="gst" className="flex items-center min-h-10">
                      GST
                      {getConfidenceBadge("gst")}
                    </Label>
                    <Input
                      id="gst"
                      type="number"
                      data-testid="gst-input"
                      value={derivedBreakdown.gst || 0}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          gst: parseFloat(e.target.value) || 0,
                          gst_manual_override: true,
                        })
                      }
                      className={`font-mono mt-1 ${getConfidenceColor("gst")}`}
                      step="0.01"
                    />
                  </div>
                  <div className="min-w-0">
                    <Label
                      htmlFor="gross_amount"
                      className="flex items-center min-h-10"
                    >
                      Gross / Total Amount
                      {getConfidenceBadge("total_amount")}
                    </Label>
                    <Input
                      id="gross_amount"
                      type="number"
                      data-testid="total-amount-input"
                      value={
                        derivedBreakdown.gross_amount ||
                        derivedBreakdown.total_amount ||
                        0
                      }
                      readOnly
                      className={`font-mono mt-1 bg-muted/40 ${getConfidenceColor("total_amount")}`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 items-end">
                  <div className="min-w-0">
                    <Label
                      htmlFor="cgst"
                      className="flex items-center min-h-10 whitespace-nowrap"
                    >
                      CGST
                    </Label>
                    <Input
                      id="cgst"
                      type="number"
                      value={derivedBreakdown.cgst || 0}
                      readOnly
                      className="font-mono mt-1 bg-muted/40"
                    />
                  </div>
                  <div className="min-w-0">
                    <Label
                      htmlFor="sgst"
                      className="flex items-center min-h-10 whitespace-nowrap"
                    >
                      SGST
                    </Label>
                    <Input
                      id="sgst"
                      type="number"
                      value={derivedBreakdown.sgst || 0}
                      readOnly
                      className="font-mono mt-1 bg-muted/40"
                    />
                  </div>
                  <div className="min-w-0">
                    <Label
                      htmlFor="igst"
                      className="flex items-center min-h-10 whitespace-nowrap"
                    >
                      IGST
                    </Label>
                    <Input
                      id="igst"
                      type="number"
                      value={derivedBreakdown.igst || 0}
                      readOnly
                      className="font-mono mt-1 bg-muted/40"
                    />
                  </div>
                  <div className="min-w-0">
                    <Label className="flex items-center min-h-10 whitespace-nowrap">
                      GST Type
                    </Label>
                    <Input
                      value={
                        derivedBreakdown.gst_breakdown_type === "igst"
                          ? "IGST"
                          : "CGST + SGST"
                      }
                      readOnly
                      className="font-mono mt-1 bg-muted/40"
                    />
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="bg-muted/30 p-4 rounded-sm">
                <p className="text-sm font-medium text-[#0B2B5C] mb-2">
                  Confidence Level Guide:
                </p>
                <div className="flex flex-wrap gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-[#10B981] rounded"></div>
                    <span>≥ 90% High</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-[#F59E0B] rounded"></div>
                    <span>70-89% Medium</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-[#EF4444] rounded"></div>
                    <span>&lt; 70% Low</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
